import {
  DigitalAssetWithToken,
  Metadata,
  fetchAllDigitalAssetWithTokenByOwner,
} from "@metaplex-foundation/mpl-token-metadata"
import { Umi, publicKey } from "@metaplex-foundation/umi"
import { PublicKey } from "@solana/web3.js"

export type StringPublicKey = string
export type MetaDataJsonCategory = "image" | "video" | "audio" | "vr" | "html"
export type MetadataJsonAttribute = {
  trait_type: string
  value: string
}
export type MetadataJsonCollection = {
  name: string
  family: string
}
export type MetadataJsonFile = {
  uri: string
  type: string
  cdn?: boolean
}
export type MetadataJsonCreator = {
  address: StringPublicKey
  verified: boolean
  share: number
}
export type MetadataJsonProperties = {
  files: MetadataJsonFile[]
  category: MetaDataJsonCategory
  creators: MetadataJsonCreator[]
}
export type MetadataJson = {
  name: string
  symbol: string
  description: string
  seller_fee_basis_points: number
  image: string
  animation_url?: string
  external_url?: string
  attributes?: MetadataJsonAttribute[]
  collection?: MetadataJsonCollection
  properties: MetadataJsonProperties
}

export type MetadataJSONResponseType = MetadataJson | undefined

export type FetchNftDetailResponseType = {
  json: MetadataJSONResponseType
  metadata: Metadata
}

const IMAGE_MIME_TYPE_REGEX = /data:image\/(svg\+xml|png|jpeg|gif)/g

export const fetchNftAssetsByCollectionsSolana = async (
  umi: Umi,
  wallet: PublicKey,
  collectionKeys: string[],
): Promise<DigitalAssetWithToken[]> => {
  const metaplexPubkey = publicKey(wallet.toBase58())
  const assets = await fetchAllDigitalAssetWithTokenByOwner(umi, metaplexPubkey)
  const assetsByCollection = assets.filter((a) => {
    return a.metadata.collection.__option === "Some" && collectionKeys.includes(a.metadata.collection.value.key)
  })
  return assetsByCollection
}

export const getMetaDataJSON = async (uri?: string): Promise<MetadataJSONResponseType> => {
  return new Promise((resolve) => {
    if (!uri) return resolve(undefined)

    const processJson = (extended: any) => {
      if (!extended || (!extended.image && extended?.properties?.files?.length === 0)) {
        return
      }
      if (extended?.image) {
        extended.image =
          extended.image.startsWith("http") || IMAGE_MIME_TYPE_REGEX.test(extended.image)
            ? extended.image
            : `${uri}/${extended.image}`
      }

      return extended
    }

    try {
      fetch(uri)
        .then(async (_) => {
          try {
            const data = await _.json()
            resolve(processJson(data))
          } catch {
            resolve(undefined)
          }
        })
        .catch(() => {
          resolve(undefined)
        })
    } catch (ex) {
      console.error(ex)
      resolve(undefined)
    }
  })
}

export const fetchNFTDetailSolana = async (metadata: Metadata): Promise<FetchNftDetailResponseType | null> => {
  try {
    let nftData = null
    if (metadata) {
      const metadataJSON = await getMetaDataJSON(metadata.uri)
      nftData = {
        json: metadataJSON,
        metadata,
      }
    }
    return nftData
  } catch (error) {
    return null
  }
}
